import { type CSSProperties } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Icon } from "@/components/ui/Icon";
import { StatusPill } from "@/components/ui/StatusPill";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import { useResolvedWorkspaceColor } from "@/hooks/useResolvedWorkspaceColor";
import { TicketAgeChip, TicketIdChip } from "@/components/board/TicketChips";
import { ticketLabel } from "@/lib/boardView";
import { BRAND } from "@/lib/tokens";
import { relPast } from "@/lib/format";
import { t } from "@/lib/strings";
import type { MySubmissionItem, UUID } from "@/lib/types";

interface Props {
  item: MySubmissionItem;
  isDesktop: boolean;
  onOpen: (boardId: UUID, rowId: UUID) => void;
}

export function PostfachCard({ item, isDesktop, onOpen }: Props) {
  const qc = useQueryClient();

  const tint = useResolvedWorkspaceColor(item.workspace_id);
  const title = item.title || t.postfachUntitled;
  const hasUnread = item.unread_notification_count > 0;
  const idLabel = ticketLabel(item.ticket_prefix, item.ticket_number);

  function openRow() {
    if (hasUnread) {
      void api.markRowNotificationsRead(item.row_id as UUID).then(() => {
        void qc.invalidateQueries({ queryKey: queryKeys.notifications });
        void qc.invalidateQueries({ queryKey: queryKeys.notificationsUnread });
        void qc.invalidateQueries({ queryKey: queryKeys.mySubmissions });
      });
    }
    onOpen(item.board_id as UUID, item.row_id as UUID);
  }

  const padX = isDesktop ? 16 : 12;
  const padY = isDesktop ? 14 : 12;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={openRow}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openRow();
        }
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "var(--surface-soft)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "var(--surface)";
      }}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        padding: `${padY}px ${padX}px`,
        border: "1px solid var(--hairline)",
        borderLeft: `3px solid ${tint}`,
        borderRadius: 10,
        background: "var(--surface)",
        cursor: "pointer",
        fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
        color: "var(--ink-1)",
        transition: "background .12s, border-color .12s, box-shadow .12s",
        outline: "none",
      }}
    >
      {/* Top row: workspace + created time + unread dot */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: 999,
            background: tint,
            flexShrink: 0,
          }}
        />
        <span style={topMetaText}>{item.board_name}</span>
        {idLabel ? <TicketIdChip label={idLabel} fontSize={11} /> : null}
        <span style={{ ...topMetaText, color: "var(--ink-4)" }}>·</span>
        <span style={{ ...topMetaText, color: "var(--ink-4)" }}>
          {t.postfachCardSubmittedPrefix} {relPast(item.created_at)}
        </span>
        <div style={{ flex: 1 }} />
        {hasUnread ? (
          <span
            aria-label={t.postfachCardUnreadAria}
            title={t.postfachCardUnreadAria}
            style={{
              width: 7,
              height: 7,
              borderRadius: 999,
              background: BRAND.orange,
              flexShrink: 0,
              boxShadow: `0 0 0 3px ${BRAND.orange}33`,
            }}
          />
        ) : null}
      </div>

      {/* Title row */}
      <div
        style={{
          fontSize: 15,
          fontWeight: 600,
          color: "var(--ink-1)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          letterSpacing: "-0.005em",
        }}
      >
        {title}
      </div>

      {/* Status + comment count row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        {item.current_status_label ? (
          <StatusPill
            option={{
              label: item.current_status_label,
              color: item.current_status_color ?? "gray",
            }}
            size="sm"
            pillStyle="tint"
          />
        ) : null}
        {item.board_kind === "tickets" ? (
          <TicketAgeChip createdAt={item.created_at} resolvedAt={item.resolved_at} />
        ) : null}
        {item.comment_count > 0 ? (
          <span
            style={{
              fontSize: 11.5,
              color: "var(--ink-4)",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <Icon name="dotsV" size={11} color="var(--ink-4)" />
            {item.comment_count === 1
              ? t.postfachCardCommentCountOne
              : t.postfachCardCommentCount.replace("{count}", String(item.comment_count))}
          </span>
        ) : null}
      </div>

      {/* Last reply preview */}
      {item.last_comment_at ? (
        <div
          style={{
            padding: "8px 10px",
            borderRadius: 7,
            background: "var(--surface-soft)",
            fontSize: 12.5,
            color: "var(--ink-2)",
            overflow: "hidden",
            display: "flex",
            gap: 6,
            alignItems: "baseline",
          }}
        >
          <span style={{ fontWeight: 600, color: "var(--ink-1)", flexShrink: 0 }}>
            {item.last_comment_author_display_name ?? "—"}
          </span>
          <span style={{ color: "var(--ink-4)", flexShrink: 0, fontSize: 11.5 }}>
            {relPast(item.last_comment_at)}
          </span>
          <span
            style={{
              fontStyle: "italic",
              color: "var(--ink-3)",
              flex: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {item.last_comment_body_excerpt}
          </span>
        </div>
      ) : (
        <div
          style={{
            fontSize: 12,
            color: "var(--ink-4)",
            fontStyle: "italic",
          }}
        >
          {t.postfachCardNoActivity}
        </div>
      )}
    </div>
  );
}

const topMetaText: CSSProperties = {
  fontSize: 11.5,
  color: "var(--ink-3)",
  fontWeight: 500,
};
